import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo } from "react";
import { filter, pick, reduce, values } from "ramda";
import { PositionRequirementFormFields } from "@sinch/components";
import { isNilOrEmpty } from "ramda-adjunct";
import {
  RequirementFulfillQuery,
  RequirementFulfillQueryVariables,
} from "@sinch/components/PositionsRequirements/queries.types";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { RequirementFulfill } from "@sinch/components/PositionsRequirements/queries.graphql";
import { debounce, Typography } from "@mui/material";

export interface StaffCountProps {
  requirements: PositionRequirementFormFields[];
  effectiveDate?: Date;
}

export const StaffCount = ({ requirements, effectiveDate }: StaffCountProps) => {
  const { t } = useTranslation();

  const filled = useMemo(
    () =>
      reduce(
        (acc, requirement: PositionRequirementFormFields) => {
          const requirementParams = pick(["operator", "requirementId", "subject"], requirement);
          if (isNilOrEmpty(filter(isNilOrEmpty, values(requirementParams)))) {
            acc.push({ ...requirementParams, value: requirement.value });
          }
          return acc;
        },
        [] as Pick<PositionRequirementFormFields, "operator" | "requirementId" | "subject" | "value">[],
        requirements ?? ([] as PositionRequirementFormFields[])
      ),
    [requirements]
  );

  const queryVariables = useMemo<RequirementFulfillQueryVariables>(
    () => ({ filter: { requirementList: filled, effectiveDate } }),
    [filled]
  );

  const { data, dispatch, refetch } = useLazyQueryHandler<RequirementFulfillQuery, RequirementFulfillQueryVariables>(
    RequirementFulfill,
    queryVariables
  );

  useEffect(() => {
    debounce(dispatch, 500)(queryVariables);
  }, [queryVariables, refetch, dispatch]);

  return (
    <Typography
      sx={{
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {t("NewJobForm.fulfillsRequirement")}:{" "}
      <Typography
        component="span"
        sx={{
          fontWeight: 700,
        }}
      >
        {data?.requirementFulfill ?? 0}
      </Typography>
    </Typography>
  );
};
